import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, CircularProgress, TextField, Typography,
} from '@mui/material';
import { useVenue } from '../../../../context/VenueContext';
import OrderSheet from '../../../../components/venue/OrderSheet';
import orderSheetService, { type OrderSheetResponse } from '../../../../services/orderSheetService';
import purchaseOrderService from '../../../../services/purchaseOrderService';
import venueSupplierService from '../../../../services/venueSupplierService';

/**
 * PO-SHEET.1 — DOOR A, supplier-first.
 *
 *   Purchasing → New order → pick supplier → the sheet
 *
 * The other door is the alerts workspace, which lands on the same component
 * with the same law. One sheet, two ways in.
 */

interface SupplierOption { _id: string; name: string }

const OrderSheetPage: React.FC = () => {
  const { venueId } = useVenue();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [supplier, setSupplier] = useState<SupplierOption | null>(null);
  const [sheet, setSheet] = useState<OrderSheetResponse | null>(null);
  const [query, setQuery] = useState('');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!venueId) return;
    venueSupplierService.listVenueSuppliers(venueId)
      .then(res => {
        const list = (Array.isArray(res) ? res : (res as { data?: unknown }).data ?? []) as SupplierOption[];
        setSuppliers(list);
        const preset = searchParams.get('supplierId');
        if (preset) {
          const hit = list.find(s => s._id === preset);
          if (hit) setSupplier(hit);
        }
      })
      .catch(() => setError('Could not load suppliers.'));
  }, [venueId, searchParams]);

  const load = useCallback(async (q: string) => {
    if (!venueId || !supplier) return;
    setLoading(true);
    setError(null);
    try {
      setSheet(await orderSheetService.get(venueId, supplier._id, { q }));
    } catch {
      setError('Could not load the order sheet.');
    } finally {
      setLoading(false);
    }
  }, [venueId, supplier]);

  // Server-side search, so it works past the page boundary.
  useEffect(() => {
    if (!supplier) { setSheet(null); return undefined; }
    const t = setTimeout(() => { load(query); }, query ? 300 : 0);
    return () => clearTimeout(t);
  }, [supplier, query, load]);

  /**
   * THE LAW, at the boundary: only filled lines become PO lines. An untouched
   * line is not sent with a zero — it is not sent.
   */
  const create = async () => {
    if (!venueId || !supplier || !sheet) return;
    const lines = sheet.lines
      .map(l => ({ id: l.inventoryItemId, qty: quantities[l.inventoryItemId] ?? 0 }))
      .filter(l => l.qty > 0);
    if (lines.length === 0) return;

    setCreating(true);
    setError(null);
    try {
      const po = await purchaseOrderService.createOrder(venueId, { supplierId: supplier._id });
      for (const l of lines) {
        await purchaseOrderService.createLine(venueId, po._id, {
          inventoryItemId: l.id,
          orderedQty: l.qty,
        });
      }
      navigate(`/admin/venues/${venueId}/purchasing/${po._id}`);
    } catch {
      setError('The order sheet is intact — creating the draft failed. Try again.');
      setCreating(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5" fontWeight={700}>New order</Typography>

      {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}

      <Autocomplete
        options={suppliers}
        value={supplier}
        onChange={(_e, v) => { setSupplier(v); setQuantities({}); setQuery(''); }}
        getOptionLabel={o => o.name}
        isOptionEqualToValue={(a, b) => a._id === b._id}
        sx={{ maxWidth: 420 }}
        renderInput={params => <TextField {...params} size="small" label="Supplier" />}
      />

      {!supplier && (
        <Typography variant="body2" color="text.secondary">
          Pick a supplier to open their order sheet.
        </Typography>
      )}

      {supplier && !sheet && loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {supplier && sheet && (
        <OrderSheet
          sheet={sheet}
          loading={loading}
          query={query}
          onQueryChange={setQuery}
          quantities={quantities}
          onQuantitiesChange={setQuantities}
          onCreate={create}
          creating={creating}
        />
      )}
    </Box>
  );
};

export default OrderSheetPage;
